import { useCallback, isValidElement, Fragment } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';

export const StyledKey = styled(Typography)(({ theme, keyWidth, keyMinWidth }) => ({
  color: theme.palette.text.secondary,
  textTransform: 'capitalize',
  width: keyWidth,
  minWidth: keyMinWidth,
  whiteSpace: 'nowrap'
}));

export const StyledValue = styled(Typography)(({ theme, valueAltColor, valueMaxWidth, oneLinerValue }) => ({
  color: valueAltColor || theme.palette.text.primary,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  marginLeft: theme.spacing(0.5),
  maxWidth: valueMaxWidth,
  cursor: 'default'
}));

/** Builds a plain string for aria-label when `value` is JSX (fragments, spans, etc.). */
function getAriaLabelTextFromValue(node) {
  if (node == null || node === '') return '';
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getAriaLabelTextFromValue).join('');
  }
  if (isValidElement(node)) {
    if (node.type === Fragment) {
      return getAriaLabelTextFromValue(node.props?.children);
    }
    if (node.props?.children != null) {
      const fromChildren = getAriaLabelTextFromValue(node.props.children).trim();
      if (fromChildren) {
        return fromChildren;
      }
    }
    const { text, url } = node.props || {};
    const fromProps = text || url;
    if (fromProps != null && fromProps !== '') {
      return String(fromProps);
    }
    return '';
  }
  return String(node);
}

function NRKeyValueDataDisplay({
  uniqueKey,
  dataKey,
  value,
  keyWidth = '40%',
  minWidth = 168,
  valueMaxWidth = 100,
  showDash,
  showTooltip = false,
  tooltipValue,
  wrapLine = false,
  hasCopy = false,
  valueAltColor,
  endAdornment,
  setIconLeft = false,
  sx = {}
}) {
  const { enqueueSnackbar } = useSnackbar();
  const valuePrefix = showDash ? ' — ' : '';
  const key = uniqueKey || dataKey + value;

  let valueText;
  if (value == null || value === '') {
    valueText = showDash ? '—' : 'No value';
  } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    valueText = String(value);
  } else if (isValidElement(value)) {
    const extracted = getAriaLabelTextFromValue(value).trim();
    valueText = extracted || (showDash ? '—' : 'No value');
  } else {
    valueText = String(value);
  }
  const rowAriaLabel = `${dataKey}: ${valueText}`;

  const onCopyThumbprint = useCallback(
    (title, value) => {
      navigator.clipboard.writeText(value);
      enqueueSnackbar(`${title} copied!`, {
        variant: 'success',
        preventDuplicate: true,
        autoHideDuration: 1000
      });
    },
    [enqueueSnackbar]
  );

  return (
    <Box
      tabIndex={0}
      key={key}
      aria-label={rowAriaLabel}
      sx={{
        display: 'flex',
        mx: 0,
        my: 0.5,
        '&:first-child': {
          marginTop: 0
        },
        ...sx
      }}
    >
      {!wrapLine ? (
        <>
          <StyledKey variant="subtitle2" keyWidth={keyWidth} keyMinWidth={minWidth} children={dataKey + valuePrefix} />
          <Tooltip
            tabIndex={0}
            title={showTooltip ? tooltipValue || value : null}
            slotProps={{
              tooltip: {
                sx: {
                  maxHeight: 400
                }
              }
            }}
          >
            <StyledValue variant="body2" valueAltColor={valueAltColor} valueMaxWidth={valueMaxWidth}>
              {value}
            </StyledValue>
          </Tooltip>
          {hasCopy && (
            <StyledValue component={'div'} variant="body2" valueAltColor={valueAltColor} valueMaxWidth={valueMaxWidth}>
              <FileCopyOutlinedIcon
                tabIndex={0}
                aria-label={`Copy ${dataKey}`}
                sx={{ width: '0.875rem', height: '0.875rem', marginLeft: 0.75, marginBottom: -0.25, cursor: 'pointer', color: 'palette.text.icon' }}
                onClick={() => onCopyThumbprint(dataKey, value)}
              />
            </StyledValue>
          )}
          {endAdornment && <Box sx={{ marginLeft: setIconLeft ? 1 : 'auto', marginRight: setIconLeft ? 'auto' : 1 }}>{endAdornment}</Box>}
        </>
      ) : (
        <Box>
          <StyledKey variant="subtitle2" keyWidth={keyWidth} keyMinWidth={minWidth} children={dataKey + valuePrefix} />
          <Tooltip tabIndex={0} title={showTooltip ? tooltipValue || value : ''}>
            <Typography variant="body2" color={!!valueAltColor ? valueAltColor : 'text.secondary'} component={'span'} sx={{ ml: 0.5 }}>
              {value}
            </Typography>
          </Tooltip>
          {hasCopy && (
            <StyledValue component={'div'} variant="body2" valueAltColor={valueAltColor} valueMaxWidth={valueMaxWidth}>
              <FileCopyOutlinedIcon
                tabIndex={0}
                aria-label={`Copy ${dataKey}`}
                sx={{ width: '0.875rem', height: '0.875rem', marginLeft: 0.75, marginBottom: -0.25, cursor: 'pointer', color: 'palette.text.icon' }}
                onClick={() => onCopyThumbprint(dataKey, value)}
              />
            </StyledValue>
          )}
        </Box>
      )}
    </Box>
  );
}

export default NRKeyValueDataDisplay;
